import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import CancelIcon from "../../../assets/icons/cross-bold-icon.png";
import GlobalButton from "../../../components/button";
import ImageUploader from "../../../components/imageUploader";
import Label from "../../../components/label";
import AppLoader from "../../../components/loader";
import TextInput from "../../../components/textInput/index";
import { createAgent, updateAgent } from "../../../services/AgentApi";
import {
  decryptPassword,
  encryptPassword,
  handleApiCall,
} from "../../../utils/helper";
import { registerAgentValidation } from "../../../utils/Validation";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));
let paramsData = {};

const AddEditAgentDialog = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState({
    image: null,
    username: "",
    email: "",
    contact: "",
    dailyTarget: 0,
    sellsTarget: 0,
    password: "",
    confirmPassword: "",
    colorCode: "",
  });

  const [inputError, setInputError] = useState({
    image: false,
    username: false,
    email: false,
    isValidEmail: false,
    contact: false,
    dailyTarget: false,
    sellsTarget: false,
    password: false,
    confirmPassword: false,
    isPasswordMatch: false,
    colorCode: false,
  });

  useImperativeHandle(ref, () => ({
    isVisible(params) {
      paramsData = params;
      setOpen(true);
      if (params.type == "edit") {
        let editPassword = decryptPassword(params.data.encrypted);
        setInputValue({
          image: params.data.image,
          username: params.data.username,
          email: params.data.email,
          contact: params.data.contact,
          dailyTarget: params.data.dailyTarget,
          sellsTarget: params.data.sellsTarget,
          colorCode: params.data.colorCode || "#000000",
          password: editPassword,
          confirmPassword: editPassword,
        });
      }
    },
    disable() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValue({});
    setInputError({});
  };

  const handleSetImage = (url) => {
    setInputValue((preValue) => ({ ...preValue, image: url }));
    setInputError((preError) => ({ ...preError, image: false }));
  };

  const handleChangeValue = (event) => {
    const { name, value } = event.target;
    setInputError((preError) => ({
      ...preError,
      [name]: false,
      isValidEmail: name === "email" ? false : inputError.isValidEmail,
      isPasswordMatch:
        name === "confirmPassword" ? false : inputError.isPasswordMatch,
    }));
    setInputValue((preValue) => ({ ...preValue, [name]: value }));
  };

  const addEditAgents = () => {
    const validate = registerAgentValidation(inputValue, setInputError);
    if (validate) {
      return;
    }

    let encryptedPass = encryptPassword(inputValue.password);

    let payload = {
      ...inputValue,
      password: encryptedPass,
      designation: "Sales Agent",
    };

    if (paramsData.type == "add") {
      handleApiCall(
        "",
        createAgent,
        payload,
        setIsLoading,
        handleClose,
        paramsData.getData,
      );
    } else {
      handleApiCall(
        paramsData.data._id,
        updateAgent,
        payload,
        setIsLoading,
        handleClose,
        paramsData.getData,
      );
    }

    // const payload = {};
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Stack
          p={2}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography
            variant="h1"
            fontSize={"1.5rem"}
            fontWeight={500}
            color={"secondary.dark"}
          >
            {paramsData.type === "add" ? "Add Agent" : "Edit Agent"}
          </Typography>
          <IconButton onClick={handleClose}>
            <img src={CancelIcon} alt="cancel-icon" width={25} />
          </IconButton>
        </Stack>
        <DialogContent dividers>
          <ImageUploader
            image={inputValue.image}
            isError={inputError.image}
            setIsLoading={setIsLoading}
            setImage={handleSetImage}
          />

          {/* <HexColorPicker color={inputValue.colorCode} onChange={(color)=>{

            setInputValue((prev)=>({...prev,colorCode:color}))
            setInputError((prev)=>({...prev,colorCode:}))
          }} />; */}

          <Box component={"form"} maxWidth={500} margin={"auto"}>
            <Grid container spacing={2} my={3}>
              <Grid item xs={12}>
                <Label title={"Agent Name"} required htmlFor={"agent-name"} />
                <TextInput
                  id={"agent-name"}
                  type={"text"}
                  required
                  error={inputError.username}
                  helperText={inputError.username && "Username is required"}
                  defaultStyle={true}
                  placeholder={"Enter agent name"}
                  value={inputValue.username}
                  name={"username"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label title={"Agent Email"} required htmlFor={"agent-email"} />
                <TextInput
                  id={"agent-email"}
                  type={"email"}
                  required
                  error={inputError.email || inputError.isValidEmail}
                  helperText={
                    inputError.email
                      ? "Email is required"
                      : inputError.isValidEmail
                        ? "Invalid email format"
                        : null
                  }
                  defaultStyle={true}
                  placeholder={"Enter agent email"}
                  value={inputValue.email}
                  name={"email"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  title={"Agent Contact"}
                  required
                  htmlFor={"agent-contact"}
                />
                <TextInput
                  id={"agent-contact"}
                  type={"number"}
                  required
                  error={inputError.contact}
                  helperText={inputError.contact && "Contact is required"}
                  defaultStyle={true}
                  placeholder={"Agent contact"}
                  value={inputValue.contact}
                  name={"contact"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label title={"Designation"} required htmlFor={"designation"} />
                <TextInput
                  id={"designation"}
                  type={"text"}
                  required
                  defaultStyle={true}
                  placeholder={"Sales agent"}
                  value="Sales Agent"
                  readonly={true}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  title={"Daily GP Target"}
                  required
                  htmlFor={"daily-target"}
                />
                <TextInput
                  id={"daily-target"}
                  type={"number"}
                  required
                  error={inputError.dailyTarget}
                  helperText={inputError.dailyTarget && "Input daily target"}
                  defaultStyle={true}
                  placeholder={"Enter daily target"}
                  value={inputValue.dailyTarget}
                  name={"dailyTarget"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  title={"Sales per day (SPD)"}
                  required
                  htmlFor={"sales-per-day"}
                />
                <TextInput
                  id={"sales-per-day"}
                  type={"number"}
                  required
                  error={inputError.sellsTarget}
                  helperText={
                    inputError.sellsTarget && "Input Sales per day (SPD)"
                  }
                  defaultStyle={true}
                  placeholder={"Enter Sales per day (SPD)"}
                  value={inputValue.sellsTarget}
                  name={"sellsTarget"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label title={"Color Code"} required htmlFor={"color-code"} />
                <TextInput
                  id={"color-code"}
                  type={"color"}
                  required
                  error={inputError.colorCode}
                  helperText={inputError.colorCode && "Color code is required"}
                  defaultStyle={true}
                  placeholder={"Enter color code"}
                  value={inputValue.colorCode}
                  name={"colorCode"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  title={"Set Password"}
                  required
                  htmlFor={"agent-password"}
                />
                <TextInput
                  id={"agent-password"}
                  type={"text"}
                  required
                  error={inputError.password}
                  helperText={inputError.password && "Password is required"}
                  defaultStyle={true}
                  placeholder={"Enter password"}
                  value={inputValue.password}
                  name={"password"}
                  onChange={handleChangeValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  title={"Re-Password"}
                  required
                  htmlFor={"confirm-agent-password"}
                />
                <TextInput
                  id={"confirm-agent-password"}
                  type={"text"}
                  required
                  error={
                    inputError.confirmPassword || inputError.isPasswordMatch
                  }
                  helperText={
                    inputError.confirmPassword
                      ? "Re-enter passoword is required"
                      : inputError.isPasswordMatch
                        ? "Password is not matched"
                        : null
                  }
                  defaultStyle={true}
                  placeholder={"Re-enter password"}
                  value={inputValue.confirmPassword}
                  name={"confirmPassword"}
                  onChange={handleChangeValue}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <AgentOffDays
                  data={paramsData.data}
                  handleClose={handleClose}
                  setIsLoading={setIsLoading}
                />
              </Grid> */}
            </Grid>
            {/* <GlobalButton
              variant={"custom_gradient"}
              handleClickBtn={addEditAgents}
              title={
                paramsData.type === "add" ? "Create New Agent" : "Edit Agent"
              }
            /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant={"custom_gradient"}
            handleClickBtn={addEditAgents}
            title={
              paramsData.type === "add" ? "Create New Agent" : "Edit Agent"
            }
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default AddEditAgentDialog;
